import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Eye, Edit, Copy, Star, Sparkles, Mail, Calendar,
  Users, TrendingUp, Palette, Settings, Play,
  ChevronLeft, ChevronRight, X, Wand2, Download
} from 'lucide-react';
import { Button } from './ui/Button/Button';
import { cn } from '../utils/cn';
import TemplateService, { HTMLTemplate, PersonalizationData } from '../services/templateService';

interface TemplateGalleryProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectTemplate: (template: HTMLTemplate, personalizedHTML?: string) => void;
  recipientData?: PersonalizationData;
  showPersonalization?: boolean;
}

export const TemplateGallery: React.FC<TemplateGalleryProps> = ({
  isOpen,
  onClose,
  onSelectTemplate,
  recipientData,
  showPersonalization = true
}) => {
  const [templates, setTemplates] = useState<HTMLTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<HTMLTemplate | null>(null);
  const [previewHTML, setPreviewHTML] = useState<string>('');
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [previewMode, setPreviewMode] = useState<'desktop' | 'mobile'>('desktop');

  useEffect(() => {
    if (isOpen) {
      const availableTemplates = TemplateService.getTemplates();
      setTemplates(availableTemplates);
    }
  }, [isOpen]);

  const categories = [
    { id: 'all', label: 'All Templates', count: templates.length },
    { id: 'sales', label: 'Sales Outreach', count: templates.filter(t => t.category === 'sales').length },
    { id: 'followup', label: 'Follow-up', count: templates.filter(t => t.category === 'followup').length },
    { id: 'calendar', label: 'Meeting Booking', count: templates.filter(t => t.category === 'calendar').length },
    { id: 'professional', label: 'Professional', count: templates.filter(t => t.category === 'professional').length }
  ];

  const filteredTemplates = activeCategory === 'all' 
    ? templates 
    : templates.filter(t => t.category === activeCategory);

  const handleTemplateClick = async (template: HTMLTemplate) => {
    setSelectedTemplate(template);
    setIsGeneratingPreview(true);

    try {
      let htmlPreview: string;
      
      if (recipientData && showPersonalization) {
        // Generate personalized preview
        htmlPreview = await TemplateService.personalizeTemplate({
          templateId: template.id,
          recipientData,
          campaignObjective: 'Generate qualified leads',
          tone: 'professional',
          urgency: 'medium',
          includeIndustryInsights: true,
          includeSocialProof: true,
          includePersonalizedValue: true
        });
      } else {
        // Generate sample preview
        htmlPreview = await TemplateService.generateTemplatePreview(template.id);
      }
      
      setPreviewHTML(htmlPreview);
    } catch (error) {
      console.error('Failed to generate preview:', error);
      setPreviewHTML('<p>Preview not available</p>');
    } finally {
      setIsGeneratingPreview(false);
    }
  };

  const handleSelectTemplate = () => {
    if (selectedTemplate) {
      onSelectTemplate(selectedTemplate, previewHTML);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-gradient-to-br from-neutral-900/95 to-neutral-800/95 backdrop-blur-xl border border-neutral-700/50 rounded-2xl w-full max-w-7xl max-h-[95vh] overflow-hidden shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-neutral-700/50">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
              <Palette className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Beautiful Email Templates</h2>
              <p className="text-neutral-400 text-sm">
                Choose from professionally designed, responsive templates
                {recipientData && ` personalized for ${recipientData.firstName}`}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-neutral-400 hover:text-white hover:bg-neutral-700/50 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex h-[calc(95vh-100px)]">
          {/* Template List */}
          <div className="w-1/2 border-r border-neutral-700/50 flex flex-col">
            {/* Category Filter */}
            <div className="p-4 border-b border-neutral-700/50">
              <div className="flex flex-wrap gap-2">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setActiveCategory(category.id)}
                    className={cn(
                      'px-3 py-2 text-xs font-medium rounded-lg transition-all duration-200',
                      activeCategory === category.id
                        ? 'bg-blue-500 text-white shadow-lg'
                        : 'text-neutral-400 hover:text-white hover:bg-neutral-700/50'
                    )}
                  >
                    {category.label} ({category.count})
                  </button>
                ))}
              </div>
            </div>

            {/* Template Grid */}
            <div className="flex-1 p-4 overflow-y-auto">
              <div className="grid grid-cols-1 gap-4">
                {filteredTemplates.map((template) => (
                  <motion.div
                    key={template.id}
                    whileHover={{ scale: 1.02 }}
                    onClick={() => handleTemplateClick(template)}
                    className={cn(
                      "p-4 bg-neutral-800/30 border border-neutral-700/50 rounded-xl cursor-pointer transition-all duration-200",
                      selectedTemplate?.id === template.id 
                        ? "border-blue-500/50 bg-blue-500/10" 
                        : "hover:border-neutral-600/50"
                    )}
                  >
                    <div className="flex items-start space-x-4">
                      {/* Template Preview Thumbnail */}
                      <div className="w-16 h-12 bg-gradient-to-br from-neutral-700 to-neutral-600 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Mail className="w-6 h-6 text-white" />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="font-semibold text-white text-sm truncate">
                            {template.name}
                          </h3>
                          <div className="flex items-center space-x-1">
                            {template.themes.length > 0 && (
                              <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                            )}
                            <Star className="w-3 h-3 text-yellow-400" />
                          </div>
                        </div>
                        
                        <p className="text-neutral-400 text-xs mb-3 line-clamp-2">
                          {template.description}
                        </p>
                        
                        {/* Features */}
                        <div className="flex flex-wrap gap-1 mb-2">
                          {template.features.slice(0, 3).map((feature, index) => (
                            <span
                              key={index}
                              className="px-2 py-1 bg-neutral-700/50 text-neutral-300 text-xs rounded-full"
                            >
                              {feature}
                            </span>
                          ))}
                          {template.features.length > 3 && (
                            <span className="px-2 py-1 bg-neutral-700/50 text-neutral-400 text-xs rounded-full">
                              +{template.features.length - 3}
                            </span>
                          )}
                        </div>
                        
                        {/* Category Badge */}
                        <div className="flex items-center justify-between">
                          <span className={cn(
                            "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium",
                            template.category === 'sales' ? "bg-blue-500/20 text-blue-400" :
                            template.category === 'followup' ? "bg-green-500/20 text-green-400" :
                            template.category === 'calendar' ? "bg-purple-500/20 text-purple-400" :
                            "bg-orange-500/20 text-orange-400"
                          )}>
                            {template.category}
                          </span>
                          
                          {recipientData && (
                            <div className="flex items-center space-x-1 text-xs text-purple-400">
                              <Sparkles className="w-3 h-3" />
                              <span>AI Personalized</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>

          {/* Template Preview */}
          <div className="w-1/2 flex flex-col">
            {/* Preview Header */}
            <div className="p-4 border-b border-neutral-700/50 flex items-center justify-between">
              <div>
                {selectedTemplate ? (
                  <>
                    <h3 className="font-semibold text-white">{selectedTemplate.name}</h3>
                    <p className="text-neutral-400 text-sm">{selectedTemplate.description}</p>
                  </>
                ) : (
                  <p className="text-neutral-400">Select a template to preview</p>
                )}
              </div>
              
              {selectedTemplate && (
                <div className="flex items-center space-x-2">
                  <div className="flex items-center space-x-1 bg-neutral-800/50 rounded-lg p-1">
                    <button
                      onClick={() => setPreviewMode('desktop')}
                      className={cn(
                        'px-3 py-1 text-xs rounded-md transition-colors',
                        previewMode === 'desktop' ? 'bg-blue-500 text-white' : 'text-neutral-400 hover:text-white'
                      )}
                    >
                      Desktop
                    </button>
                    <button
                      onClick={() => setPreviewMode('mobile')}
                      className={cn(
                        'px-3 py-1 text-xs rounded-md transition-colors',
                        previewMode === 'mobile' ? 'bg-blue-500 text-white' : 'text-neutral-400 hover:text-white'
                      )}
                    >
                      Mobile
                    </button>
                  </div>
                  
                  <Button
                    onClick={handleSelectTemplate}
                    size="sm"
                    className="bg-gradient-to-r from-blue-500 to-purple-600"
                  >
                    <Play className="w-4 h-4 mr-2" />
                    Use Template
                  </Button>
                </div>
              )}
            </div>

            {/* Preview Content */}
            <div className="flex-1 p-4 overflow-hidden">
              {selectedTemplate ? (
                <div className="h-full flex items-center justify-center">
                  {isGeneratingPreview ? (
                    <div className="text-center">
                      <Sparkles className="w-8 h-8 text-purple-400 mx-auto mb-3 animate-spin" />
                      <p className="text-white font-medium">
                        {recipientData ? 'Personalizing template...' : 'Generating preview...'}
                      </p>
                      <p className="text-neutral-400 text-sm">This may take a moment</p>
                    </div>
                  ) : previewHTML ? (
                    <div className={cn(
                      "bg-white rounded-lg shadow-2xl overflow-hidden transition-all duration-300",
                      previewMode === 'mobile' ? 'w-80 h-full' : 'w-full h-full'
                    )}>
                      <iframe
                        srcDoc={previewHTML}
                        className="w-full h-full border-none"
                        title="Email Preview"
                        sandbox="allow-same-origin"
                      />
                    </div>
                  ) : (
                    <div className="text-center">
                      <Mail className="w-16 h-16 text-neutral-500 mx-auto mb-4" />
                      <p className="text-white font-medium mb-2">Preview Unavailable</p>
                      <p className="text-neutral-400 text-sm">
                        Click to regenerate template preview
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="h-full flex items-center justify-center">
                  <div className="text-center">
                    <Palette className="w-16 h-16 text-neutral-500 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-white mb-2">
                      Beautiful Email Templates
                    </h3>
                    <p className="text-neutral-400 mb-6 max-w-md">
                      Select any template from the left to see a live preview. 
                      {recipientData && ' Templates will be automatically personalized for your recipient.'}
                    </p>
                    <div className="flex items-center justify-center space-x-6 text-sm text-neutral-500">
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                        <span>Mobile Responsive</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                        <span>AI Personalized</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                        <span>Professional Design</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}; 